import React, { useEffect, useState } from "react";
import { Box, styled } from "@mui/material";
import StewartTable from "controls/global/stewart-table";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import InputSearch from "../../InputSearch";
import { colors, padding } from "theme/defaultStyle";
import { Order } from "utils/sorting";
import { UnderwriterItem } from "../SelectPreferredUnderwriterDialog";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";

interface PreferredUnderwriterTableProps {
  underwriterItems: UnderwriterItem[];
  selectedUnderwriter: UnderwriterItem | null;
  setSelectedUnderwriter: (underwriter: UnderwriterItem | null) => void;
  schema: string;
}

const TableWrapper = styled(Box)({
  height: "100%",
  maxHeight: "calc(100vh - 380px)",
  overflow: "hidden",
  overflowX: "hidden",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  "& .MuiBox-root.MuiTableContainer-root": {
    minHeight: "unset !important",
    maxHeight: "calc(100vh - 380px) !important",
    overflow: "auto !important",
    overflowX: "hidden !important",
  },
  "& .header-actions > th:first-of-type": {
    paddingLeft: padding.small1,
  },
  "& tr:hover td": {
    background: colors.grey10,
  },
  "& tbody .MuiTableCell-root": {
    padding: "19px 8px",
  },
  "& tbody tr td:first-of-type": {
    backgroundColor: "transparent !important",
  },
  "& tbody tr:hover td:first-of-type": {
    backgroundColor: `${colors.grey10} !important`,
  },
});

export default function PreferredUnderwriterTable({
  underwriterItems = [],
  selectedUnderwriter,
  setSelectedUnderwriter,
  schema,
}: PreferredUnderwriterTableProps) {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] =
    React.useState<keyof UnderwriterItem>("lastName");
  const [searchCriteria, setSearchCriteria] = useState({
    lastName: "",
    firstName: "",
    email: "",
  });

  const { getValues } = useFormWrapper();

  const userId = getValues(`${schema}.preferredUnderwriter`);

  useEffect(() => {
    if (!userId) {
      setSelectedUnderwriter(null);
    }
  }, [userId]);

  const filteredUnderwriterItems =
    underwriterItems?.filter((item) => {
      const lastNameMatch =
        item?.lastName
          ?.toLowerCase()
          ?.includes(searchCriteria?.lastName?.toLowerCase() || "") ?? false;
      const firstNameMatch =
        item?.firstName
          ?.toLowerCase()
          ?.includes(searchCriteria?.firstName?.toLowerCase() || "") ?? false;
      const emailMatch =
        item?.email
          ?.toLowerCase()
          ?.includes(searchCriteria?.email?.toLowerCase() || "") ?? false;

      return lastNameMatch && firstNameMatch && emailMatch;
    }) || [];

  const handleRequestSort = (columnName: keyof UnderwriterItem) => {
    if (orderBy === columnName) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrder("asc");
      setOrderBy(columnName);
    }
  };

  const columns: any[] = [
    {
      field: "lastName",
      name: "LAST NAME",
      sortable: true,
      width: 250,
      valueGetter: (row: UnderwriterItem) => {
        return (
          <Box>
            <TooltipCell title={row?.lastName || ""}></TooltipCell>
          </Box>
        );
      },
      actionComponent: () => (
        <InputSearch
          name="lastName"
          defaultValue=""
          onChange={(_name: string, value: string | null) => {
            setSearchCriteria((prev) => ({
              ...prev,
              lastName: value || "",
            }));
          }}
        />
      ),
    },
    {
      field: "firstName",
      name: "FIRST NAME",
      sortable: true,
      width: 250,
      valueGetter: (row: UnderwriterItem) => {
        return (
          <Box>
            <TooltipCell title={row?.firstName || ""}></TooltipCell>
          </Box>
        );
      },
      actionComponent: () => (
        <InputSearch
          name="firstName"
          defaultValue=""
          onChange={(_name: string, value: string | null) => {
            setSearchCriteria((prev) => ({
              ...prev,
              firstName: value || "",
            }));
          }}
        />
      ),
    },
    {
      field: "email",
      name: "EMAIL",
      sortable: true,
      width: 300,
      valueGetter: (row: UnderwriterItem) => {
        return (
          <Box>
            <TooltipCell title={row?.email || ""}></TooltipCell>
          </Box>
        );
      },
      actionComponent: () => (
        <InputSearch
          name="email"
          defaultValue=""
          onChange={(_name: string, value: string | null) => {
            setSearchCriteria((prev) => ({
              ...prev,
              email: value || "",
            }));
          }}
        />
      ),
    },
  ];

  return (
    <TableWrapper>
      <StewartTable
        cols={columns}
        rows={filteredUnderwriterItems || []}
        rowsPerPage={999999}
        noRowsMessage={["No Underwriters available"]}
        page={1}
        order={order}
        orderBy={orderBy}
        onColumnResize={() => {}}
        onRequestSort={handleRequestSort}
        onClickRow={(row) => {
          setSelectedUnderwriter(row as UnderwriterItem);
        }}
        highlightedRowId={
          userId ? userId : selectedUnderwriter?.uniqueIdentifier
        }
      />
    </TableWrapper>
  );
}
